'use client'

import { useState, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'
import { buttonClasses, type ButtonSize, type ButtonVariant } from '@/components/ui/Button'
import { SITE } from '@/lib/site'

const CALENDLY_URL = SITE.bookingUrl

/**
 * Where a booking button sits. Calendly records UTM parameters on the scheduling
 * URL against the booking, so each booking can be traced to the page and the
 * placement it came from — the site's own analytics never sees inside the iframe.
 */
export type BookingUtm = { campaign: string; content: string; term?: string }

function bookingSrc(base: string, utm?: BookingUtm): string {
  if (!utm) return base
  const url = new URL(base)
  url.searchParams.set('utm_source', 'refinity.io')
  url.searchParams.set('utm_medium', 'website')
  url.searchParams.set('utm_campaign', utm.campaign)
  url.searchParams.set('utm_content', utm.content)
  if (utm.term) url.searchParams.set('utm_term', utm.term)
  return url.toString()
}

type Props = {
  label?: string
  size?: ButtonSize
  variant?: Exclude<ButtonVariant, 'link'>
  className?: string
  onOpen?: () => void
  /** Override the booking URL. Defaults to the site-wide booking link. */
  url?: string
  utm?: BookingUtm
}

export default function CalendlyModal({
  label = 'Book a Call',
  size = 'md',
  variant = 'primary',
  className,
  onOpen,
  url,
  utm,
}: Props) {
  const [open, setOpen] = useState(false)
  const src = bookingSrc(url ?? CALENDLY_URL, utm)

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  return (
    <>
      <button
        type="button"
        onClick={() => {
          onOpen?.()
          setOpen(true)
        }}
        className={buttonClasses(variant, size, className)}
      >
        {label}
      </button>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
            onClick={close}
          >
            {/* theme-lock: the Calendly iframe is a white third-party surface in both themes */}
            <div
              className="relative flex h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-md bg-white"
              onClick={(e) => e.stopPropagation()}
            >
              {/* theme-lock: sits on the white iframe card */}
              <button
                type="button"
                onClick={close}
                aria-label="Close booking dialog"
                className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/10 text-sm text-black/60 transition-colors hover:bg-black/20"
              >
                ✕
              </button>
              <iframe
                src={src}
                title="Book a call with Refinity.io"
                className="h-full w-full border-0"
                loading="eager"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  )
}
